import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  FileText,
  Clock,
  DollarSign,
  Shield,
  Ban,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [unsuspendDialog, setUnsuspendDialog] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchUserDetail();
  }, [userId]);

  const fetchUserDetail = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.get(`${backendUrl}/api/admin/users/${userId}`);
      
      setUser(res.data.user);
      setContracts(res.data.contracts || []);
      setStats(res.data.statistics || {});
      setAuditLogs(res.data.audit_logs || []);
    } catch (error) {
      console.error('Erreur chargement utilisateur:', error);
      if (error.response?.status === 404) {
        toast.error('Utilisateur non trouvé');
        navigate('/admin/users');
      } else {
        toast.error('Erreur de chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!suspendReason.trim()) {
      toast.error('Veuillez indiquer une raison');
      return;
    }

    setActionLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${backendUrl}/api/admin/users/${userId}/suspend`, {
        reason: suspendReason
      });

      toast.success('Utilisateur suspendu');
      setSuspendDialog(false);
      setSuspendReason('');
      fetchUserDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur suspension');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsuspend = async () => {
    setActionLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${backendUrl}/api/admin/users/${userId}/unsuspend`, {
        reason: 'Réactivation par admin'
      });

      toast.success('Utilisateur réactivé');
      setUnsuspendDialog(false);
      fetchUserDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur réactivation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!window.confirm('Forcer la vérification de cet email ?')) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${backendUrl}/api/admin/users/${userId}/verify-email`);

      toast.success('Email vérifié');
      fetchUserDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur vérification');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Utilisateur introuvable</p>
      </div>
    );
  }

  const isAdmin = currentUser?.role === 'admin';

  return (
    <div className="space-y-6 p-6" data-testid="user-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/users')}
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{user.full_name || 'Utilisateur'}</h1>
            <p className="text-muted-foreground">{user.email}</p>
          </div>
        </div>

        {/* Admin Actions */}
        {isAdmin && (
          <div className="flex gap-2">
            {!user.email_verified && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleVerifyEmail}
                data-testid="verify-email-button"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Vérifier email
              </Button>
            )}
            
            {user.is_suspended ? (
              <Button
                variant="default"
                size="sm"
                onClick={() => setUnsuspendDialog(true)}
                data-testid="unsuspend-button"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Réactiver
              </Button>
            ) : (
              user.role !== 'admin' && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setSuspendDialog(true)}
                  data-testid="suspend-button"
                >
                  <Ban className="h-4 w-4 mr-2" />
                  Suspendre
                </Button>
              )
            )}
          </div>
        )}
      </div>

      {/* User Info */}
      <Card>
        <CardHeader>
          <CardTitle>Informations Utilisateur</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="h-4 w-4" />
                Email
              </div>
              <p className="font-medium">{user.email}</p>
              {user.email_verified ? (
                <Badge variant="secondary" className="bg-green-500/10 text-green-700">Vérifié</Badge>
              ) : (
                <Badge variant="outline">Non vérifié</Badge>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="h-4 w-4" />
                Rôle & Plan
              </div>
              <div className="flex gap-2">
                <Badge variant={user.role === 'admin' ? 'destructive' : 'default'}>
                  {user.role}
                </Badge>
                <Badge variant={user.current_plan === 'premium' ? 'default' : 'outline'}>
                  {user.current_plan}
                </Badge>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Inscription
              </div>
              <p className="font-medium">
                {user.created_at ? format(new Date(user.created_at), 'dd MMMM yyyy', { locale: fr }) : '-'}
              </p>
            </div>
          </div>

          {user.is_suspended && (
            <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
                <div>
                  <p className="font-semibold text-destructive">Compte Suspendu</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Raison : {user.suspension_reason || 'Non spécifiée'}
                  </p>
                  {user.suspension_date && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Le {format(new Date(user.suspension_date), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Contrats</p>
                <h3 className="text-2xl font-bold mt-2">{stats.contracts_count || 0}</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.active_contracts || 0} actif{stats.active_contracts > 1 ? 's' : ''}
                </p>
              </div>
              <FileText className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Heures</p>
                <h3 className="text-2xl font-bold mt-2">{stats.time_entries_count || 0}</h3>
              </div>
              <Clock className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Paies</p>
                <h3 className="text-2xl font-bold mt-2">{stats.payroll_count || 0}</h3>
              </div>
              <DollarSign className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="contracts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="contracts" data-testid="contracts-tab">
            Contrats ({contracts.length})
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="audit-tab">
            Audit ({auditLogs.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contracts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Contrats</CardTitle>
              <CardDescription>Liste des contrats de cet utilisateur</CardDescription>
            </CardHeader>
            <CardContent>
              {contracts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Aucun contrat
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Enfant</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Taux horaire</TableHead>
                      <TableHead>Créé le</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contracts.map((contract) => (
                      <TableRow key={contract.id}>
                        <TableCell className="font-medium">
                          {contract.child_first_name ? `${contract.child_first_name} ${contract.child_name}` : contract.child_name}
                        </TableCell>
                        <TableCell>
                          {contract.code ? (
                            <code className="px-2 py-1 bg-muted rounded text-sm font-mono">
                              {contract.code}
                            </code>
                          ) : (
                            <span className="text-muted-foreground text-xs">Non généré</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={contract.is_linked ? 'default' : 'outline'}>
                            {contract.is_linked ? 'Lié' : 'En attente'}
                          </Badge>
                        </TableCell>
                        <TableCell>{contract.hourly_rate}€</TableCell>
                        <TableCell>
                          {contract.created_at ? format(new Date(contract.created_at), 'dd/MM/yyyy', { locale: fr }) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Journal d'Audit</CardTitle>
              <CardDescription>Historique des actions administratives</CardDescription>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Aucune entrée d'audit
                </div>
              ) : (
                <div className="space-y-3">
                  {auditLogs.map((log, idx) => (
                    <div key={idx} className="p-3 border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-sm">{log.action}</p>
                          {log.reason && (
                            <p className="text-sm text-muted-foreground mt-1">
                              Raison : {log.reason}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            Par {log.admin_email} • {format(new Date(log.timestamp), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialog} onOpenChange={setSuspendDialog}>
        <DialogContent data-testid="suspend-dialog">
          <DialogHeader>
            <DialogTitle>Suspendre l'utilisateur</DialogTitle>
            <DialogDescription>
              Cette action empêchera l'utilisateur de se connecter.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="suspend-reason">Raison de la suspension *</Label>
              <Textarea
                id="suspend-reason"
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Expliquez la raison de la suspension..."
                rows={4}
                data-testid="suspend-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSuspendDialog(false)}
              disabled={actionLoading}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleSuspend}
              disabled={actionLoading}
              data-testid="confirm-suspend-button"
            >
              {actionLoading ? 'Suspension...' : 'Suspendre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsuspend Dialog */}
      <Dialog open={unsuspendDialog} onOpenChange={setUnsuspendDialog}>
        <DialogContent data-testid="unsuspend-dialog">
          <DialogHeader>
            <DialogTitle>Réactiver l'utilisateur</DialogTitle>
            <DialogDescription>
              L'utilisateur pourra à nouveau se connecter.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUnsuspendDialog(false)}
              disabled={actionLoading}
            >
              Annuler
            </Button>
            <Button
              onClick={handleUnsuspend}
              disabled={actionLoading}
              data-testid="confirm-unsuspend-button"
            >
              {actionLoading ? 'Réactivation...' : 'Réactiver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
